import { Preferences } from '@capacitor/preferences';
import { z } from 'zod';
import { isNative } from '@/utils/platform';
import { sharedStorage } from './sharedStorage';

const ACCESS_KEY = 'bfs_access_token';
const REFRESH_KEY = 'bfs_refresh_token';
const USER_ID_KEY = 'bfs_user_id';

const SHARED_KEYS = new Set<string>([ACCESS_KEY, REFRESH_KEY, USER_ID_KEY]);

const TokenPayloadSchema = z.object({
  access_token: z.string().min(10).max(4096),
  refresh_token: z.string().min(10).max(4096),
  user_id: z.number().int().positive(),
});

export type NativeTokenPayload = z.infer<typeof TokenPayloadSchema>;

const memoryFallback: { [k: string]: string | null } = {};

async function setItem(key: string, value: string): Promise<void> {
  if (!isNative()) {
    memoryFallback[key] = value;
    return;
  }
  await Preferences.set({ key, value });
  if (SHARED_KEYS.has(key)) {
    await sharedStorage.set(key, value);
  }
}

async function getItem(key: string): Promise<string | null> {
  if (!isNative()) {
    return memoryFallback[key] ?? null;
  }
  const { value } = await Preferences.get({ key });
  return value ?? null;
}

async function removeItem(key: string): Promise<void> {
  if (!isNative()) {
    delete memoryFallback[key];
    return;
  }
  await Preferences.remove({ key });
  if (SHARED_KEYS.has(key)) {
    await sharedStorage.remove(key);
  }
}

export const nativeTokenStore = {
  async save(payload: unknown): Promise<NativeTokenPayload> {
    const parsed = TokenPayloadSchema.parse(payload);
    await setItem(ACCESS_KEY, parsed.access_token);
    await setItem(REFRESH_KEY, parsed.refresh_token);
    await setItem(USER_ID_KEY, String(parsed.user_id));
    return parsed;
  },

  async getAccessToken(): Promise<string | null> {
    return getItem(ACCESS_KEY);
  },

  async getRefreshToken(): Promise<string | null> {
    return getItem(REFRESH_KEY);
  },

  async getUserId(): Promise<number | null> {
    const raw = await getItem(USER_ID_KEY);
    if (!raw) return null;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  },

  async setAccessToken(token: string): Promise<void> {
    if (!token || typeof token !== 'string') return;
    await setItem(ACCESS_KEY, token);
  },

  async setRefreshToken(token: string): Promise<void> {
    if (!token || typeof token !== 'string') return;
    await setItem(REFRESH_KEY, token);
  },

  async clear(): Promise<void> {
    await removeItem(ACCESS_KEY);
    await removeItem(REFRESH_KEY);
    await removeItem(USER_ID_KEY);
    await sharedStorage.clear();
  },

  async hasTokens(): Promise<boolean> {
    const access = await getItem(ACCESS_KEY);
    return !!access;
  },

  async syncToShared(): Promise<void> {
    if (!isNative()) return;
    try {
      const [access, refresh, uid] = await Promise.all([
        getItem(ACCESS_KEY),
        getItem(REFRESH_KEY),
        getItem(USER_ID_KEY),
      ]);

      console.log('[nativeTokenStore] syncToShared start', {
        hasAccess: !!access,
        hasRefresh: !!refresh,
        hasUid: !!uid,
      });

      if (access) {
        await sharedStorage.set(ACCESS_KEY, access);
      }
      if (refresh) {
        await sharedStorage.set(REFRESH_KEY, refresh);
      }
      if (uid) {
        await sharedStorage.set(USER_ID_KEY, uid);
      }

      const verifyAccess = await sharedStorage.get(ACCESS_KEY);
      const verifyUid = await sharedStorage.get(USER_ID_KEY);
      console.log('[nativeTokenStore] syncToShared verify', {
        sharedHasAccess: !!verifyAccess,
        sharedHasUid: !!verifyUid,
      });
    } catch (e) {
      console.warn('[nativeTokenStore] syncToShared failed', e);
    }
  },

  /**
   * Adopts tokens that the native share extension refreshed out-of-process.
   *
   * The share extension (iOS Share Extension / Android ShareActivity) can
   * refresh an expired access token on its own. When it does, it writes the
   * rotated tokens to the App Group store. Because the main app always writes
   * Preferences and the App Group together on every refresh, a mismatch
   * between them means the extension refreshed while the app was backgrounded.
   * In that case the extension's tokens are authoritative — the old refresh
   * token in Preferences is already invalidated server-side — so adopt them
   * before the app validates or refreshes its session.
   *
   * On Android the share target writes straight to `CapacitorStorage`, so this
   * is effectively a no-op there; it matters for iOS.
   */
  async syncFromShared(): Promise<void> {
    if (!isNative()) return;
    try {
      const [sharedAccess, sharedRefresh, sharedUid] = await Promise.all([
        sharedStorage.get(ACCESS_KEY),
        sharedStorage.get(REFRESH_KEY),
        sharedStorage.get(USER_ID_KEY),
      ]);

      // Nothing usable in the shared store — leave Preferences untouched.
      if (!sharedAccess || !sharedRefresh) return;

      const localRefresh = await getItem(REFRESH_KEY);
      if (localRefresh === sharedRefresh) return;

      console.log('[nativeTokenStore] syncFromShared adopting extension-refreshed tokens');
      await Preferences.set({ key: ACCESS_KEY, value: sharedAccess });
      await Preferences.set({ key: REFRESH_KEY, value: sharedRefresh });
      if (sharedUid) {
        await Preferences.set({ key: USER_ID_KEY, value: sharedUid });
      }
    } catch (e) {
      console.warn('[nativeTokenStore] syncFromShared failed', e);
    }
  },
};
